import { useState, useRef } from "react";
import { toast } from "sonner";
import { BookOpen, Search } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "./ui/sheet";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { AlgorithmFlow } from "./AlgorithmFlow";
import { AiLoader, BulletList } from "./common";
import { useSession, callAI, aiError } from "../lib/session";

const SYNDROMES = ["Undifferentiated shock", "Septic shock", "Acute hypoxaemic respiratory failure", "ARDS", "Acute exacerbation of COPD",
  "Coma / altered sensorium", "Status epilepticus", "Acute stroke", "Raised intracranial pressure", "Acute kidney injury",
  "Metabolic acidosis", "Diabetic ketoacidosis", "Hyperkalaemia", "Hyponatraemia", "Upper GI bleed", "Acute pancreatitis",
  "Acute liver failure", "Pulmonary embolism", "Acute coronary syndrome / cardiogenic shock", "Anaphylaxis", "Poisoning / toxidrome", "Polytrauma"];

const cache = {};

// Loads a syndrome algorithm once per model; ignores stale responses when the user clicks another syndrome.
function useLibrary(model) {
  const [sel, setSel] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const latest = useRef(null);

  const fetchInto = async (key, name) => {
    setData(null); setLoading(true);
    try {
      cache[key] = cache[key] || callAI("library", model, { syndrome: name });
      const res = await cache[key];
      cache[key] = res;
      if (latest.current === key) setData(res);
    } catch (e) {
      delete cache[key];
      if (latest.current === key) toast.error(aiError(e));
    }
    if (latest.current === key) setLoading(false);
  };

  const open = (name) => {
    const key = `${model}:${name.toLowerCase()}`;
    latest.current = key;
    setSel(name);
    if (cache[key] && !(cache[key] instanceof Promise)) { setLoading(false); setData(cache[key]); return; }
    fetchInto(key, name);
  };
  return { sel, data, loading, open };
}

const LibraryResult = ({ data }) => (
  <>
    <AlgorithmFlow algo={data.algorithm} testid="library-algorithm" />
    {data.key_points?.length > 0 && (
      <div className="border rounded-md p-4 text-sm"><div className="cc-label mb-1">Key points</div><BulletList items={data.key_points} className="list-disc pl-5" /></div>
    )}
    {data.pitfalls?.length > 0 && (
      <div className="border-l-4 border-[var(--cc-red)] p-4 text-sm"><div className="cc-label text-[var(--cc-red)] mb-1">Pitfalls</div><BulletList items={data.pitfalls} className="list-disc pl-5" /></div>
    )}
  </>
);

const SyndromeChips = ({ list, sel, onOpen }) => (
  <div className="flex flex-wrap gap-2 mt-4">
    {list.map((s) => (
      <button key={s} data-testid={`library-item-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => onOpen(s)}
        className={`cc-toggle text-xs ${sel === s ? "is-on" : ""}`}>{s}</button>
    ))}
  </div>
);

export const AlgorithmLibrary = () => {
  const { state } = useSession();
  const [q, setQ] = useState("");
  const { sel, data, loading, open } = useLibrary(state.model);
  const list = SYNDROMES.filter((s) => s.toLowerCase().includes(q.toLowerCase()));

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm" className="h-9" data-testid="btn-algorithm-library"><BookOpen className="w-4 h-4 sm:mr-1" /><span className="hidden sm:inline">Algorithms</span></Button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-3xl overflow-y-auto" data-testid="algorithm-library">
        <SheetHeader>
          <SheetTitle className="font-heading">Syndromic Algorithm Library</SheetTitle>
          <SheetDescription className="text-xs">Washington Manual-style algorithms (AI-reproduced; verify with the book).</SheetDescription>
        </SheetHeader>
        <div className="relative mt-4">
          <Search className="w-4 h-4 absolute left-3 top-3 text-muted-foreground" />
          <Input data-testid="library-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && q.trim() && open(q.trim())}
            placeholder="Search or type any syndrome and press Enter" className="pl-9 cc-input" />
        </div>
        <SyndromeChips list={list} sel={sel} onOpen={open} />
        <div className="mt-6 space-y-4">
          {loading && <AiLoader text={`Building algorithm: ${sel}`} />}
          {!loading && data && <LibraryResult data={data} />}
        </div>
      </SheetContent>
    </Sheet>
  );
};
